/**
 * Retry Plugin - Global plugin for handling API retries
 * Usage: this.$retry(apiFunction, options)
 *
 * Returns a Promise that resolves when the request succeeds, or rejects
 * when the user cancels. While waiting for the user, the promise stays pending.
 */

import { shouldOfferRetry, RETRY_CANCELLED_MESSAGE } from "../utils/apiError";
import { canCallApi } from "../utils/network";

function cleanupWaitState(reactiveState) {
  reactiveState.pendingAction = null;
  reactiveState.onCancelCallback = null;
  reactiveState._resolve = null;
  reactiveState._reject = null;
}

function isChunkLoadError(error) {
  return (
    error &&
    (error.name === "ChunkLoadError" ||
      (error.message && error.message.indexOf("Loading chunk") !== -1))
  );
}

function shouldShowRetry(error) {
  return shouldOfferRetry(error) || isChunkLoadError(error);
}

export default {
  install(Vue) {
    const reactiveState = Vue.observable({
      visible: false,
      message: "",
      title: "Error",
      showIcon: true,
      pendingAction: null,
      onCancelCallback: null,
      cancelledByUser: false,
      interactive: false,
      _resolve: null,
      _reject: null,
    });

    const resolveSuccess = (result) => {
      const resolve = reactiveState._resolve;
      cleanupWaitState(reactiveState);
      reactiveState.visible = false;
      reactiveState.message = "";
      reactiveState.interactive = false;
      reactiveState.cancelledByUser = false;
      if (resolve) {
        resolve(result);
      }
    };

    const rejectWithError = (error) => {
      const reject = reactiveState._reject;
      cleanupWaitState(reactiveState);
      reactiveState.visible = false;
      reactiveState.interactive = false;
      if (reject) {
        reject(error);
      }
    };

    Vue.prototype.$retryPopup = reactiveState;

    Vue.prototype.$hideRetryPopupUI = function () {
      reactiveState.visible = false;
    };

    Vue.prototype.$retry = function (apiFunction, options = {}) {
      const {
        errorMessage = "Request failed. Please try again.",
        errorTitle = "Error",
        showIcon = true,
        onCancel = null,
        silent = false,
        checkOfflineFirst = false,
        interactive = false,
      } = options;

      const showRetryPopup = (error) => {
        if (reactiveState.visible && !interactive) {
          return;
        }

        if (error) {
          console.error("API call failed:", error);
        }

        reactiveState.cancelledByUser = false;
        reactiveState.interactive = interactive;
        reactiveState.message = errorMessage;
        reactiveState.title = errorTitle;
        reactiveState.showIcon = showIcon;
        reactiveState.pendingAction = async () => {
          reactiveState.visible = false;
          try {
            const result = await apiFunction();
            resolveSuccess(result);
            return result;
          } catch (retryError) {
            console.error("Retry attempt failed:", retryError);
            if (!silent && shouldShowRetry(retryError)) {
              reactiveState.visible = true;
            } else {
              rejectWithError(retryError);
            }
            throw retryError;
          }
        };
        reactiveState.onCancelCallback = () => {
          if (onCancel) {
            try {
              onCancel();
            } catch (callbackError) {
              console.error("Cancel callback error:", callbackError);
            }
          }
          reactiveState.message = "";
          reactiveState.cancelledByUser = true;
          rejectWithError(new Error(RETRY_CANCELLED_MESSAGE));
        };
        reactiveState.visible = true;
      };

      return new Promise(async (resolve, reject) => {
        reactiveState._resolve = resolve;
        reactiveState._reject = reject;

        if (checkOfflineFirst) {
          const online = await canCallApi();
          if (!online) {
            if (silent) {
              rejectWithError(new Error("Network unavailable"));
              return;
            }
            showRetryPopup(null);
            return;
          }
        }

        try {
          const result = await apiFunction();
          resolveSuccess(result);
        } catch (error) {
          if (silent) {
            rejectWithError(error);
            return;
          }

          if (!shouldShowRetry(error)) {
            rejectWithError(error);
            return;
          }

          showRetryPopup(error);
        }
      });
    };

    Vue.prototype.$handleRetry = async function () {
      const pendingAction = reactiveState.pendingAction;
      if (pendingAction) {
        await pendingAction();
      }
    };

    Vue.prototype.$handleRetryCancel = function () {
      reactiveState.cancelledByUser = true;

      if (reactiveState.onCancelCallback) {
        reactiveState.onCancelCallback();
        return;
      }

      reactiveState.message = "";
      reactiveState.title = "Error";
      rejectWithError(new Error(RETRY_CANCELLED_MESSAGE));
    };

    Vue.prototype.$onNetworkReconnect = async function () {
      if (!reactiveState.pendingAction || reactiveState.cancelledByUser) {
        return;
      }

      reactiveState.visible = false;

      try {
        await reactiveState.pendingAction();
      } catch (error) {
        console.log("Auto-retry on reconnect failed:", error);
      }
    };

    Vue.prototype.$hideRetryPopup = function () {
      reactiveState.message = "";
      rejectWithError(new Error(RETRY_CANCELLED_MESSAGE));
    };
  },
};
